import { isKitchenPrinter, type PrintablePrinter } from '@/features/printing/domain/service/print-channels';

export type PrinterReachability = 'checking' | 'reachable' | 'unreachable' | 'unknown';

export type DefaultPrinterReason = 'probe' | 'subnet' | 'heuristic' | 'none';

export interface PickDefaultPrinterOptions {
  /** IPs respondidas por el driver (`/probe`). `null`/`undefined` = el driver no lo soporta. */
  reachableIps?: string[] | null;
  /** IP LAN de esta máquina (driver actualizado). Sirve para el fallback por subred. */
  hostIp?: string | null;
}

export interface DefaultPrinterResult {
  printer: PrintablePrinter | null;
  /** `true` cuando la elección se basó en la red (probe o subred), no solo en la heurística. */
  detectedInNetwork: boolean;
  reason: DefaultPrinterReason;
}

/** Devuelve la subred /24 de una IPv4 (`192.168.68.33` → `192.168.68`). `null` si no es IPv4. */
export function subnetOf(ip: string): string | null {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/.exec(ip.trim());
  if (!match) return null;

  const octets = [match[1], match[2], match[3]].map(Number);
  if (octets.some(octet => Number.isNaN(octet) || octet > 255)) return null;

  return octets.join('.');
}

export function isSameSubnet(a: string, b: string): boolean {
  const subnetA = subnetOf(a);
  const subnetB = subnetOf(b);
  return subnetA !== null && subnetA === subnetB;
}

function pickByRole(pool: PrintablePrinter[]): PrintablePrinter | null {
  const cashier = pool.find(printer => printer.type === 'cashier');
  if (cashier) return cashier;

  const cashNamed = pool.find(printer => {
    const name = printer.name.trim().toLowerCase();
    return name.includes('caja') || name.includes('cajero');
  });
  if (cashNamed) return cashNamed;

  return pool[0] ?? null;
}

/**
 * Elige la impresora por defecto priorizando las que están en la misma red que esta máquina:
 *
 * 1. Las que respondieron al `/probe` del driver (reachable), priorizando rol de caja.
 * 2. Si el driver no soporta probe: las de la misma subred que `hostIp`.
 * 3. Fallback: heurística por tipo/nombre (comportamiento histórico).
 *
 * Nunca devuelve `null` si hay impresoras cargadas: si ninguna responde, se elige igual la
 * mejor candidata para no bloquear la impresión (la UI muestra la advertencia de red).
 */
export function pickDefaultPrinter(
  printers: PrintablePrinter[],
  { reachableIps, hostIp }: PickDefaultPrinterOptions = {}
): DefaultPrinterResult {
  if (printers.length === 0) {
    return { printer: null, detectedInNetwork: false, reason: 'none' };
  }

  if (reachableIps && reachableIps.length > 0) {
    const reachable = printers.filter(printer => reachableIps.includes(printer.ip));
    if (reachable.length > 0) {
      return { printer: pickByRole(reachable), detectedInNetwork: true, reason: 'probe' };
    }
  }

  if (hostIp) {
    const sameSubnet = printers.filter(printer => isSameSubnet(printer.ip, hostIp));
    if (sameSubnet.length > 0) {
      return { printer: pickByRole(sameSubnet), detectedInNetwork: true, reason: 'subnet' };
    }
  }

  return { printer: pickByRole(printers), detectedInNetwork: false, reason: 'heuristic' };
}

/**
 * Elige la impresora de cocina: la primera `kitchen` (o nombre con "cocina") que responda en la
 * red; si no hay datos de red, la primera del local.
 */
export function pickKitchenPrinter(
  printers: PrintablePrinter[],
  { reachableIps }: { reachableIps?: string[] | null } = {}
): PrintablePrinter | null {
  const kitchens = printers.filter(isKitchenPrinter);
  if (kitchens.length === 0) return null;

  if (reachableIps && reachableIps.length > 0) {
    const reachable = kitchens.find(printer => reachableIps.includes(printer.ip));
    if (reachable) return reachable;
  }

  return kitchens[0];
}
